import { useCallback, useEffect, useState } from 'react'
import {
  Avatar,
  Button,
  Card,
  Empty,
  Input,
  List,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd'
import { ReloadOutlined, SendOutlined, UserOutlined } from '@ant-design/icons'
import {
  listMessengerConversations,
  listMessengerMessages,
  replyToMessengerConversation,
  type MessengerConversationSummary,
  type MessengerInboxMessage,
} from '../services/api'

const { Text, Paragraph } = Typography

function displayName(c: MessengerConversationSummary): string {
  return c.participantName || `Messenger user ${c.messengerPsid.slice(-6)}`
}

export function MessengerInboxCard() {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [conversations, setConversations] = useState<MessengerConversationSummary[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [messages, setMessages] = useState<MessengerInboxMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [draft, setDraft] = useState('')

  const loadConversations = useCallback(async () => {
    setLoading(true)
    try {
      const data = await listMessengerConversations()
      setConversations(data.conversations || [])
    } catch (e: unknown) {
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Failed to load Messenger inbox'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  const loadMessages = useCallback(async (conversationId: string) => {
    setMessagesLoading(true)
    try {
      const data = await listMessengerMessages(conversationId)
      setMessages(data.messages || [])
    } catch (e: unknown) {
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Failed to load messages'
      )
      setMessages([])
    } finally {
      setMessagesLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadConversations()
  }, [loadConversations])

  useEffect(() => {
    if (!selectedId) {
      setMessages([])
      return
    }
    void loadMessages(selectedId)
  }, [selectedId, loadMessages])

  const selected = conversations.find((c) => c.id === selectedId) || null

  const sendReply = async () => {
    if (!selectedId || !draft.trim()) return
    setBusy(true)
    try {
      await replyToMessengerConversation(selectedId, draft.trim())
      setDraft('')
      message.success('Message sent')
      await Promise.all([loadMessages(selectedId), loadConversations()])
    } catch (e: unknown) {
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Failed to send message'
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      className="messenger-card"
      loading={loading}
      title="Messenger inbox"
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={() => void loadConversations()}>
          Refresh
        </Button>
      }
      style={{ background: '#ffffff', borderColor: '#cbd5e1' }}
    >
      <Paragraph style={{ color: '#475569' }}>
        Send a message from this app and receive the customer reply here. Customer names/handles stay
        visible for App Review and support.
      </Paragraph>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 1fr) minmax(280px, 1.4fr)',
          gap: 12,
          minHeight: 320,
        }}
      >
        <List
          size="small"
          bordered
          locale={{ emptyText: <Empty description="No conversations yet" /> }}
          dataSource={conversations}
          renderItem={(item) => (
            <List.Item
              style={{
                cursor: 'pointer',
                background: item.id === selectedId ? 'rgba(24,144,255,0.08)' : undefined,
              }}
              onClick={() => setSelectedId(item.id)}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    src={item.participantProfilePic || undefined}
                    icon={!item.participantProfilePic ? <UserOutlined /> : undefined}
                  />
                }
                title={<Text style={{ color: '#0f172a' }}>{displayName(item)}</Text>}
                description={
                  <Space direction="vertical" size={0}>
                    <Text style={{ color: '#64748b' }} ellipsis>
                      {item.lastMessageText || 'No messages'}
                    </Text>
                    {item.pageName ? <Tag>{item.pageName}</Tag> : null}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
          }}
        >
          {!selected ? (
            <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24 }}>
              <Empty description="Select a conversation" />
            </div>
          ) : (
            <>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
                <Text strong style={{ color: '#0f172a' }}>
                  {displayName(selected)}
                </Text>
                <br />
                <Text style={{ color: '#64748b', fontSize: 12 }}>
                  Handle: {selected.participantName || selected.messengerPsid}
                  {selected.pageName ? ` · Page: ${selected.pageName}` : ''}
                </Text>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: 12, maxHeight: 260 }}>
                {messagesLoading ? (
                  <Spin />
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        justifyContent: m.direction === 'OUTBOUND' ? 'flex-end' : 'flex-start',
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '80%',
                          padding: '8px 10px',
                          borderRadius: 10,
                          background: m.direction === 'OUTBOUND' ? '#1890ff' : '#f1f5f9',
                          color: m.direction === 'OUTBOUND' ? '#fff' : '#0f172a',
                        }}
                      >
                        <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 2 }}>
                          {m.direction === 'OUTBOUND' ? 'You' : displayName(selected)}
                        </div>
                        <div>{m.text}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <Space.Compact style={{ padding: 12, width: '100%' }}>
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a Messenger reply…"
                  onPressEnter={() => void sendReply()}
                  disabled={busy}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={busy}
                  onClick={() => void sendReply()}
                >
                  Send
                </Button>
              </Space.Compact>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
